import { ref, computed, watch, nextTick } from 'vue';
import {
  useRouter,
  useRoute,
  type Router,
  type RouteLocationNormalized,
} from 'vue-router';

export interface TabItem {
  id: string;
  fullPath: string;
  path: string;
  title: string;
  name: string;
  componentName?: string;
  icon?: string;
  closable: boolean;
  createdAt: number;
}

export interface RouterTabOptions {
  homeRoute?: string;
  homeTitle?: string;
  homeIcon?: string;
  storageKey?: string;
}

const tabs = ref<TabItem[]>([]);
const visitHistory = ref<string[]>([]);
const refreshTokens = ref<Record<string, number>>({});

function getRefreshToken(fullPath: string): number {
  return refreshTokens.value[fullPath] ?? 0;
}

function bumpRefreshToken(fullPath: string) {
  refreshTokens.value[fullPath] = getRefreshToken(fullPath) + 1;
}
let isInitialized = false;
let currentOptions: RouterTabOptions = {
  homeRoute: '/',
  homeTitle: 'Dashboard',
};

function getStorageKey(key: string): string {
  const prefix = currentOptions.storageKey || 'itp/router-tab';
  return `${prefix}/${key}`;
}

function loadFromStorage(): string | null {
  try {
    const storedTabs = localStorage.getItem(getStorageKey('tabs'));
    const storedActive = localStorage.getItem(getStorageKey('active'));
    const storedHistory = localStorage.getItem(getStorageKey('history'));

    if (storedTabs) {
      tabs.value = JSON.parse(storedTabs);
    }

    if (storedHistory) {
      visitHistory.value = JSON.parse(storedHistory);
    }

    return storedActive ? JSON.parse(storedActive) : null;
  } catch (e) {
    console.warn('Failed to load from storage:', e);
    return null;
  }
}

function saveToStorage() {
  try {
    localStorage.setItem(getStorageKey('tabs'), JSON.stringify(tabs.value));
  } catch (e) {
    console.warn('Failed to save to storage:', e);
  }
}

function saveActiveToStorage(fullPath: string) {
  try {
    localStorage.setItem(getStorageKey('active'), JSON.stringify(fullPath));
  } catch (e) {
    console.warn('Failed to save active to storage:', e);
  }
}

function saveHistoryToStorage() {
  try {
    localStorage.setItem(
      getStorageKey('history'),
      JSON.stringify(visitHistory.value),
    );
  } catch (e) {
    console.warn('Failed to save history to storage:', e);
  }
}

function pushVisitHistory(fullPath: string) {
  const idx = visitHistory.value.indexOf(fullPath);
  if (idx !== -1) {
    visitHistory.value.splice(idx, 1);
  }
  visitHistory.value.push(fullPath);
}

function pruneVisitHistory() {
  const valid = new Set(tabs.value.map((t) => t.fullPath));
  visitHistory.value = visitHistory.value.filter((p) => valid.has(p));
}

function findPreviousOpenTab(excludePath: string): TabItem | null {
  const byPath = new Map(tabs.value.map((t) => [t.fullPath, t]));
  for (let i = visitHistory.value.length - 1; i >= 0; i--) {
    const p = visitHistory.value[i];
    if (p === excludePath) {
      continue;
    }
    const tab = byPath.get(p);
    if (tab) {
      return tab;
    }
  }
  return null;
}

function refreshStoredTabsFromRoutes(router: Router) {
  for (const tab of tabs.value) {
    if (!tab.fullPath) {
      continue;
    }
    try {
      const resolved = router.resolve(tab.fullPath);
      if (!resolved || resolved.matched.length === 0) {
        continue;
      }

      let metaTitle: string | undefined;
      let metaIcon: string | undefined;
      for (let i = resolved.matched.length - 1; i >= 0; i--) {
        const record = resolved.matched[i];
        const t = record.meta?.title as string | undefined;
        if (metaTitle === undefined && t && t !== '..') {
          metaTitle = t;
        }
        const ic = record.meta?.icon as string | undefined;
        if (metaIcon === undefined && ic) {
          metaIcon = ic;
        }
        if (metaTitle !== undefined && metaIcon !== undefined) {
          break;
        }
      }

      if (metaTitle) {
        tab.title = metaTitle;
      }
      if (metaIcon) {
        tab.icon = metaIcon;
      }
    } catch (e) {
      // route may no longer exist;
      console.log('Route no longer exists: ', e);
    }
  }
}

export function useRouterTab(options?: RouterTabOptions) {
  const router = useRouter();
  const route = useRoute();

  if (options) {
    currentOptions = { ...currentOptions, ...options };
  }

  if (!isInitialized) {
    isInitialized = true;

    const storedActive = loadFromStorage();

    watch(tabs, () => saveToStorage(), { deep: true });
    watch(visitHistory, () => saveHistoryToStorage(), { deep: true });

    ensureHomeTab();
    refreshStoredTabsFromRoutes(router);

    if (
      storedActive &&
      storedActive !== route.fullPath &&
      tabs.value.some((t) => t.fullPath === storedActive)
    ) {
      router.replace(storedActive).catch(() => {});
    }
  }

  function ensureHomeTab() {
    const homeRoute = currentOptions.homeRoute || '/';
    const existingHome = tabs.value.find(
      (t) => t.path === homeRoute || t.fullPath === homeRoute,
    );

    if (!existingHome) {
      try {
        const resolved = router.resolve(homeRoute);
        const metaTitle = resolved.meta?.title as string;
        const homeTab: TabItem = {
          id: homeRoute,
          fullPath: resolved.fullPath || homeRoute,
          path: homeRoute,
          title:
            metaTitle && metaTitle !== '..'
              ? metaTitle
              : currentOptions.homeTitle || 'Dashboard',
          name: (resolved.name as string) || '',
          icon: currentOptions.homeIcon || (resolved.meta?.icon as string),
          closable: false,
          createdAt: 0,
        };
        tabs.value.unshift(homeTab);
      } catch (e) {
        const homeTab: TabItem = {
          id: homeRoute,
          fullPath: homeRoute,
          path: homeRoute,
          title: currentOptions.homeTitle || 'Dashboard',
          name: '',
          icon: currentOptions.homeIcon,
          closable: false,
          createdAt: 0,
        };
        tabs.value.unshift(homeTab);
      }
    } else {
      existingHome.closable = false;
      const index = tabs.value.indexOf(existingHome);
      if (index > 0) {
        tabs.value.splice(index, 1);
        tabs.value.unshift(existingHome);
      }
    }
  }

  function getTabTitle(routeInfo: RouteLocationNormalized): string {
    const metaTitle = routeInfo.meta?.title as string;
    if (metaTitle && metaTitle !== '..') {
      return metaTitle;
    }

    if (routeInfo.name) {
      const name = routeInfo.name.toString();
      const last =
        name
          .replace(/^app\./, '')
          .split('.')
          .pop() || name;
      return last
        .replace(/[-_]/g, ' ')
        .replace(/([A-Z])/g, ' $1')
        .replace(/^./, (str) => str.toUpperCase())
        .trim();
    }

    return routeInfo.path;
  }

  function getComponentName(routeInfo: RouteLocationNormalized): string {
    const matched = routeInfo.matched;
    if (matched && matched.length > 0) {
      const last = matched[matched.length - 1];
      const comp = last.components?.default;
      if (comp && typeof comp === 'object' && 'name' in comp) {
        return (comp as any).name || '';
      }
    }
    return '';
  }

  function addTab(routeInfo?: RouteLocationNormalized) {
    const targetRoute = routeInfo || route;
    const { fullPath, path, meta, name } = targetRoute;

    const existing = tabs.value.find((t) => t.fullPath === fullPath);
    if (existing) {
      if (!existing.componentName) {
        existing.componentName = getComponentName(targetRoute);
      }
      pushVisitHistory(fullPath);
      saveActiveToStorage(fullPath);
      return existing;
    }

    const tabKey = meta?.key as string;
    if (tabKey && tabKey !== 'path') {
      const parentTab = tabs.value.find(
        (t) => t.fullPath === tabKey || t.fullPath.startsWith(tabKey + '/'),
      );
      if (parentTab) {
        const oldPath = parentTab.fullPath;
        parentTab.fullPath = fullPath;
        if (oldPath !== fullPath) {
          const oldIdx = visitHistory.value.indexOf(oldPath);
          if (oldIdx !== -1) {
            visitHistory.value.splice(oldIdx, 1);
          }
        }
        pushVisitHistory(fullPath);
        saveActiveToStorage(fullPath);
        return parentTab;
      }
    }

    const isHome =
      path === currentOptions.homeRoute ||
      fullPath === currentOptions.homeRoute;

    const newTab: TabItem = {
      id: fullPath,
      fullPath,
      path,
      title: getTabTitle(targetRoute),
      name: (name as string) || '',
      componentName: getComponentName(targetRoute),
      icon: meta?.icon as string,
      closable: isHome ? false : meta?.closable !== false,
      createdAt: Date.now(),
    };

    if (isHome) {
      const homeIndex = tabs.value.findIndex((t) => !t.closable);
      if (homeIndex >= 0) {
        tabs.value.splice(homeIndex, 1, newTab);
      } else {
        tabs.value.unshift(newTab);
      }
    } else {
      tabs.value.push(newTab);
    }

    pushVisitHistory(fullPath);
    saveActiveToStorage(fullPath);
    return newTab;
  }

  function closeTab(fullPath: string) {
    const index = tabs.value.findIndex((t) => t.fullPath === fullPath);
    if (index === -1) {
      return;
    }

    const tab = tabs.value[index];

    if (!tab.closable) {
      return;
    }

    const wasActive = route.fullPath === fullPath;

    tabs.value.splice(index, 1);
    pruneVisitHistory();

    if (wasActive) {
      const previous = findPreviousOpenTab(fullPath);
      if (previous) {
        router.push(previous.fullPath);
      } else {
        const fallback = tabs.value[index] || tabs.value[index - 1];
        if (fallback) {
          router.push(fallback.fullPath);
        } else if (currentOptions.homeRoute) {
          router.push(currentOptions.homeRoute);
        }
      }
    }

    if (tabs.value.length === 0) {
      ensureHomeTab();
      if (currentOptions.homeRoute) {
        router.push(currentOptions.homeRoute);
      }
    }
  }

  function closeOtherTabs(fullPath: string) {
    tabs.value = tabs.value.filter(
      (t) => t.fullPath === fullPath || !t.closable,
    );
    pruneVisitHistory();

    if (!tabs.value.find((t) => t.fullPath === route.fullPath)) {
      const target =
        tabs.value.find((t) => t.fullPath === fullPath) || tabs.value[0];
      if (target) {
        router.push(target.fullPath);
      }
    }
  }

  function closeAllTabs() {
    tabs.value = tabs.value.filter((t) => !t.closable);
    pruneVisitHistory();

    if (!tabs.value.find((t) => t.fullPath === route.fullPath)) {
      if (currentOptions.homeRoute) {
        router.push(currentOptions.homeRoute);
      } else if (tabs.value.length > 0) {
        router.push(tabs.value[0].fullPath);
      }
    }
  }

  function closeLeftTabs(fullPath: string) {
    const anchorIndex = tabs.value.findIndex((t) => t.fullPath === fullPath);
    if (anchorIndex <= 0) {
      return;
    }
    tabs.value = tabs.value.filter((t, i) => i >= anchorIndex || !t.closable);
    pruneVisitHistory();

    if (!tabs.value.find((t) => t.fullPath === route.fullPath)) {
      const previous = findPreviousOpenTab(route.fullPath);
      const target =
        previous ||
        tabs.value.find((t) => t.fullPath === fullPath) ||
        tabs.value[0];
      if (target) {
        router.push(target.fullPath);
      }
    }
  }

  function closeRightTabs(fullPath: string) {
    const anchorIndex = tabs.value.findIndex((t) => t.fullPath === fullPath);
    if (anchorIndex < 0 || anchorIndex >= tabs.value.length - 1) {
      return;
    }
    tabs.value = tabs.value.filter((t, i) => i <= anchorIndex || !t.closable);
    pruneVisitHistory();

    if (!tabs.value.find((t) => t.fullPath === route.fullPath)) {
      const previous = findPreviousOpenTab(route.fullPath);
      const target =
        previous ||
        tabs.value.find((t) => t.fullPath === fullPath) ||
        tabs.value[0];
      if (target) {
        router.push(target.fullPath);
      }
    }
  }

  function hasClosableLeftOf(fullPath: string): boolean {
    const idx = tabs.value.findIndex((t) => t.fullPath === fullPath);
    if (idx <= 0) {
      return false;
    }
    return tabs.value.slice(0, idx).some((t) => t.closable);
  }

  function hasClosableRightOf(fullPath: string): boolean {
    const idx = tabs.value.findIndex((t) => t.fullPath === fullPath);
    if (idx < 0) {
      return false;
    }
    return tabs.value.slice(idx + 1).some((t) => t.closable);
  }

  async function refreshAllTabs() {
    for (const tab of tabs.value) {
      bumpRefreshToken(tab.fullPath);
    }
    await nextTick();
  }

  async function refreshTab(fullPath: string) {
    const tab = tabs.value.find((t) => t.fullPath === fullPath);
    if (!tab) {
      return;
    }

    const originalComponentName = tab.componentName;
    const originalName = tab.name;
    tab.componentName = '';
    tab.name = '';

    bumpRefreshToken(fullPath);

    await nextTick();
    tab.componentName = originalComponentName;
    tab.name = originalName;
  }

  function reorderTabs(fromIndex: number, toIndex: number) {
    const fromTab = tabs.value[fromIndex];
    const toTab = tabs.value[toIndex];

    if (
      !fromTab?.closable ||
      (toTab && !toTab.closable && toIndex < fromIndex)
    ) {
      return;
    }

    const [moved] = tabs.value.splice(fromIndex, 1);
    tabs.value.splice(toIndex, 0, moved);
  }

  function updateTabTitle(fullPath: string, title: string) {
    const tab = tabs.value.find((t) => t.fullPath === fullPath);
    if (tab && title) {
      tab.title = title;
    }
  }

  function updateTabIcon(fullPath: string, icon: string) {
    const tab = tabs.value.find((t) => t.fullPath === fullPath);
    if (tab) {
      tab.icon = icon;
    }
  }

  const cachedViews = computed(() => {
    return tabs.value.map((t) => t.componentName || t.name).filter((n) => !!n);
  });

  const activeTab = computed(() => {
    return tabs.value.find((t) => t.fullPath === route.fullPath) || null;
  });

  return {
    tabs,
    activeTab,
    addTab,
    closeTab,
    closeOtherTabs,
    closeAllTabs,
    closeLeftTabs,
    closeRightTabs,
    hasClosableLeftOf,
    hasClosableRightOf,
    refreshTab,
    refreshAllTabs,
    getRefreshToken,
    reorderTabs,
    updateTabTitle,
    updateTabIcon,
    cachedViews,
    ensureHomeTab,
  };
}
